/**
 * Consent API — pass-through wrappers over
 *   `POST   /consent`
 *   `GET    /consent/{id}`
 *   `POST   /consent/{id}/revoke`
 *
 * These surfaces are entirely new on the UI side. The functions
 * accept already-signed tokens / revocations because the caller
 * (a future "issue consent" dialog) will build and sign them
 * in-browser — the server never sees the subject's private key.
 *
 * All three throw [`ApiHttpError`] on a non-2xx response:
 *   - `.notFound` === `true` on 404 (unknown or revoked consent).
 *   - `.status === 409` on duplicate consent_id.
 *   - `.status === 422` on signature verification failure.
 *   - `.status === 400` on body/path consent_id mismatch.
 */

import type {
  ConsentId,
  CreateConsentResponse,
  SignedConsentRevocation,
  SignedConsentToken,
} from '../types';
import { ApiHttpError, apiFetch } from '../http';

export async function createConsent(
  signed: SignedConsentToken,
): Promise<CreateConsentResponse> {
  return apiFetch<CreateConsentResponse>('/consent', {
    method: 'POST',
    body: signed,
  });
}

export async function getConsent(
  consent_id: ConsentId,
): Promise<SignedConsentToken | null> {
  try {
    return await apiFetch<SignedConsentToken>(
      `/consent/${encodeURIComponent(consent_id)}`,
    );
  } catch (err) {
    if (err instanceof ApiHttpError && err.notFound) return null;
    throw err;
  }
}

export async function revokeConsent(
  consent_id: ConsentId,
  signed: SignedConsentRevocation,
): Promise<void> {
  await apiFetch<void>(`/consent/${encodeURIComponent(consent_id)}/revoke`, {
    method: 'POST',
    body: signed,
  });
}
